import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import { MenuInput, Button, AlertBanner, Loader } from '@saviynt/design-system';
import { saveConnection } from '../../redux/migrationThunks';

const toOptions = (items) =>
  items.map((item) => ({
    label: item.connectionName || item,
    value: item.connectionName || item,
    isChecked: false,
  }));

function GlobalConfiguration({
  sourceType,
  classes,
  oimConnectionOptions,
  sailpointConnectionOptions,
  connectionOptionsLoading,
  connectionDetails,
  onConnectionSaved,
}) {
  const dispatch = useDispatch();

  const isOIM = sourceType?.toLowerCase().includes('oim');

  const apiData = isOIM ? oimConnectionOptions : sailpointConnectionOptions;
  const connectionOptions = apiData?.length ? toOptions(apiData) : [];

  const connectionLabel = isOIM ? 'OIM Connection' : 'SailPoint Connection';

  const [selectedConnection, setSelectedConnection] = useState(
    connectionDetails[sourceType]?.connectionName ?? null
  );
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ visible: false, type: 'Success', message: '' });

  const handleConnectionChange = (clicked) => {
    if (clicked?.isChecked) {
      setSelectedConnection(clicked.value);
    } else {
      setSelectedConnection(null);
    }
  };

  const handleSave = () => {
    if (!selectedConnection) {
      setBanner({
        visible: true,
        type: 'Critical',
        message: 'Please select a connection before saving.',
      });
      return;
    }
    setSaving(true);
    dispatch(
      saveConnection({
        configKey: isOIM ? 'CONNECTION_FOR_OIM' : 'CONNECTION_FOR_SAILPOINT_IIQ',
        connectionName: selectedConnection,
        category: isOIM ? 'OIM' : 'Sailpoint',
      })
    )
      .unwrap()
      .then(() => {
        if (onConnectionSaved) onConnectionSaved(selectedConnection);
        setBanner({ visible: true, type: 'Success', message: 'Configuration saved successfully.' });
      })
      .catch((err) => {
        setBanner({
          visible: true,
          type: 'Critical',
          message: err?.message || 'Failed to save configuration.',
        });
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className={classes.globalConfigWrapper}>
      {banner.visible && (
        <AlertBanner
          colorTheme={banner.type}
          title={banner.type === 'Success' ? 'Success' : 'Error'}
          description={banner.message}
          isVisible={banner.visible}
          isFloating
          position={{ top: '16px', right: '16px' }}
          onCancel={() => setBanner((prev) => ({ ...prev, visible: false }))}
          shouldAutoDismiss
          autoDismissDelay={5000}
          isCloseButtonVisible
          className={classes.alertBanner}
        />
      )}
      {saving && (
        <div className={classes.saveOverlay}>
          <Loader kind="loop" size="large" format="inline" color="brand" />
        </div>
      )}
      <Typography className={classes.configSectionTitle}>Connection Settings</Typography>
      <Typography className={classes.configSectionDesc}>
        Configure the source system connection.
      </Typography>
      <div className={classes.configRows}>
        <div className={classes.configRow}>
          <div className={classes.configRowLabel}>
            <Typography className={classes.label}>{connectionLabel}</Typography>
            <Typography className={classes.configRowHint}>
              Select the connection of your source system
            </Typography>
          </div>
          <div className={classes.configRowInput}>
            <MenuInput
              options={connectionOptions.map((o) => ({
                ...o,
                isChecked: o.value === selectedConnection,
              }))}
              onChange={handleConnectionChange}
              placeholder={connectionOptionsLoading ? 'Loading...' : `Select ${connectionLabel}`}
              backgroundColor="secondary"
            />
          </div>
          <Button kind="filled" size="medium" onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

GlobalConfiguration.propTypes = {
  sourceType: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  oimConnectionOptions: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  sailpointConnectionOptions: PropTypes.array,
  connectionOptionsLoading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  connectionDetails: PropTypes.object,
  onConnectionSaved: PropTypes.func,
};

GlobalConfiguration.defaultProps = {
  sourceType: null,
  oimConnectionOptions: null,
  sailpointConnectionOptions: null,
  connectionOptionsLoading: false,
  connectionDetails: {},
  onConnectionSaved: null,
};

export default GlobalConfiguration;
